import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { 
  FaArrowLeft, FaSave, FaTimes, FaTruck, FaMapMarkerAlt,
  FaWeightHanging, FaCalendarAlt, FaExclamationTriangle,
  FaFileAlt, FaBuilding, FaUser, FaPhone, FaEnvelope,
  FaSpinner, FaCheckCircle, FaCamera
} from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import { quotes } from '../api';
import InteractiveRouteMap from '../components/InteractiveRouteMap';

function ClientNewJob({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState({});
  const [client, setClient] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [me, setMe] = useState({ full_name: '', email: '', phone: '' });

  const { isLoaded, loadError } = useGoogleMaps();

  const [formData, setFormData] = useState({
    quote_type: 'minerals',
    commodity: '',
    tonnage: '',
    origin: '',
    destination: '',
    distance_km: '',
    target_price: '',
    description: '',
    services: '',
    comments: ''
  });

  // ✅ PHASE 3: Site inspection info submitted upfront with quote request
  const [siteInfo, setSiteInfo] = useState({
    access: '',
    offloading: '',
    site_contact: ''
  });
  const [siteFiles, setSiteFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchClientData(userData);
    fetchMyDetails(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The quote request belongs to the PERSON who is signed in. The company's
  // primary contact (accounts contact) is only shown as a reference + fallback.
  const fetchMyDetails = async (userData) => {
    try {
      const r = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const data = await r.json();
      if (data.success && data.user) {
        setMe({
          full_name: data.user.full_name || '',
          email: data.user.email || '',
          phone: data.user.phone || ''
        });
        return;
      }
    } catch (err) {
      console.error('Could not load your details:', err);
    }
    setMe({
      full_name: userData.full_name || '',
      email: userData.email || '',
      phone: userData.phone || ''
    });
  };

  const fetchClientData = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/clients?user_id=${userData.id}`);
      const data = await response.json();
      if (data.success && data.clients && data.clients.length > 0) {
        const clientData = data.clients[0];
        setClient(clientData);
        setClientId(clientData.id);
      }
    } catch (err) {
      console.error('Error fetching client data:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ✅ PHASE 3: Upload site files before submitting
  const uploadSiteFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return [];

    const uploadedUrls = [];
    setUploadingFiles(true);
    try {
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('image', file);
        const res = await fetch(`${API_URL}/uploads`, {
          method: 'POST',
          body: formData
        });
        const data = await res.json();
        if (data.success && data.url) {
          uploadedUrls.push(data.url);
        }
      }
    } catch (err) {
      console.error('Site file upload error:', err);
    } finally {
      setUploadingFiles(false);
    }
    return uploadedUrls;
  };

  const handleSiteFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    setSiteFiles(prev => [...prev, ...selected]);
  };

  const removeSiteFile = (index) => {
    setSiteFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate based on type
    if (formData.quote_type === 'minerals' || formData.quote_type === 'agri') {
      if (!formData.commodity || !formData.tonnage || !formData.origin || !formData.destination) {
        setError('Please fill in all required fields (commodity, tonnage, origin, destination)');
        setLoading(false);
        return;
      }
    } else if (formData.quote_type === 'abnormal') {
      if (!formData.description) {
        setError('Please provide a job description for abnormal loads');
        setLoading(false);
        return;
      }
    } else if (formData.quote_type === 'general') {
      if (!formData.services) {
        setError('Please describe the services you need');
        setLoading(false);
        return;
      }
    }

    try {
      // Upload site files first (if any)
      const siteFileUrls = await uploadSiteFiles(siteFiles);

      // The request is made by whoever is signed in (name / email / cell from
      // their own profile) — NOT the company's accounts contact.
      // A new cell number is saved back to the profile so it prefills next time.
      const currentPhone = (user.phone || '').trim();
      if ((me.phone || '').trim() && (me.phone || '').trim() !== currentPhone) {
        try {
          await fetch(`${API_URL}/users/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`
            },
            body: JSON.stringify({ full_name: me.full_name || user.full_name, phone: me.phone })
          });
        } catch (e) {
          console.warn('Could not save the cell number to your profile:', e);
        }
      }

      // Build the payload for a quote request
      const payload = {
        quote_type: formData.quote_type,
        customer_id: clientId,
        customer_name: client?.company_name || '',
        contact_person: me.full_name || user.full_name || client?.contact_person || '',
        contact_phone: (me.phone || '').trim() || client?.contact_phone || '',
        contact_email: me.email || user.email || client?.contact_email || '',
        created_by: user.id,
        commodity: formData.commodity || null,
        tonnage: formData.tonnage ? parseFloat(formData.tonnage) : null,
        origin: formData.origin || null,
        destination: formData.destination || null,
        distance_km: formData.distance_km ? parseFloat(formData.distance_km) : null,
        rate_per_ton: formData.target_price ? parseFloat(formData.target_price) : null,
        description: formData.description || null,
        services: formData.services || null,
        comments: formData.comments || null,
        payment_terms: '30 Days',
        site_answers: siteInfo,
        site_files: siteFileUrls,
        // >>> ROUTE CAPTURE: the route the client drew/dragged on the map, stored with the
        // request so the load pages can redraw EXACTLY what they were shown. <<<
        route: (formData.origin || formData.destination) ? {
          pickup_address: formData.origin || null,
          delivery_address: formData.destination || null,
          distance_km: formData.distance_km ? parseFloat(formData.distance_km) : null,
          route_optimized: formData.route_optimized || null
        } : null
      };

      console.log('📩 Submitting quote request:', payload);

      // Use the new client-request endpoint
      const response = await quotes.clientRequest(payload);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/client-quotes');
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit quote request');
      }
    } catch (err) {
      console.error('Error submitting quote request:', err);
      setError('Failed to submit quote request');
    } finally {
      setLoading(false);
    }
  };

  if (loadError) {
    console.warn('Google Maps failed to load:', loadError);
  }

  if (success) {
    return (
      <ClientLayout activeTab="newjob" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-6xl mb-4">📩</div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Quote Request Submitted!</h2>
            <p className="text-gray-500">Your quote request has been sent to One Purpose Logistics.</p>
            <p className="text-gray-400 text-sm mt-2">Redirecting to your quotes...</p>
          </div>
        </div>
      </ClientLayout>
    );
  }

  const mapsReady = isLoaded && !loadError;

  // Render type-specific form sections
  const renderTypeFields = () => {
    switch (formData.quote_type) {
      case 'minerals':
      case 'agri':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commodity <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="commodity"
                value={formData.commodity}
                onChange={handleChange}
                placeholder={formData.quote_type === 'minerals' ? 'e.g. Chrome, Manganese, Coal' : 'e.g. Maize, Wheat, Grain'}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tonnage (MT) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="tonnage"
                value={formData.tonnage}
                onChange={handleChange}
                placeholder="e.g. 500"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
                min="1"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Price (R/MT) <span className="text-xs text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                name="target_price"
                value={formData.target_price}
                onChange={handleChange}
                placeholder="e.g. 450"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                min="0"
                step="0.01"
              />
            </div>
          </>
        );
      case 'abnormal':
        return (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Describe the abnormal load/job requirements (dimensions, weight, special handling, etc.)..."
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
              required
            />
          </div>
        );
      case 'general':
        return (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Services Required <span className="text-red-500">*</span>
            </label>
            <textarea
              name="services"
              value={formData.services}
              onChange={handleChange}
              rows="4"
              placeholder="Describe the services you need a quote for..."
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
              required
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <ClientLayout activeTab="newjob" onLogout={onLogout} user={user}>
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate('/client-dashboard')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-navy"
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-navy">
            📄 Request a Quote
          </h1>
          <p className="text-sm text-gray-500">
            Submit a new quote request — {client?.company_name || 'your company'}
          </p>
        </div>
      </div>

      {/* 📦 Container Estimator banner — entry point to the client self-serve estimator */}
      <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="text-3xl">📦</div>
          <div>
            <h2 className="text-lg font-bold text-navy">Need a container moved?</h2>
            <p className="text-sm text-gray-600">
              Get an instant estimate with our AI assistant — 6m, 12m, empty or loaded — then request an official quote.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/client-estimator')}
          className="px-6 py-3 bg-yellow-400 text-navy rounded-lg text-sm font-bold hover:bg-yellow-300 transition-colors shadow whitespace-nowrap"
        >
          🚚 Container Estimator
        </button>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <form onSubmit={handleSubmit}>
          {/* Who this request is for */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500">Company</p>
                <p className="font-medium text-navy">{client?.company_name || 'Loading...'}</p>
                {client?.contact_person ? (
                  <p className="text-xs text-gray-400 mt-0.5">Primary contact: {client.contact_person}</p>
                ) : null}
              </div>
              <div>
                <p className="text-xs text-gray-500">Your name</p>
                <p className="font-medium text-navy">{me.full_name || user.full_name || 'N/A'}</p>
                <p className="text-xs text-gray-400 mt-0.5">{me.email || user.email || ''}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500" htmlFor="contact-phone">Your cell number</label>
                <input
                  id="contact-phone"
                  type="tel"
                  value={me.phone || ''}
                  onChange={(e) => setMe((prev) => ({ ...prev, phone: e.target.value }))}
                  placeholder="e.g. 082 123 4567"
                  className="mt-1 w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none"
                  style={{ borderColor: '#E8ECEF' }}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Used for delivery updates. Saved to your profile — edit it any time under My Profile.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              <FaExclamationTriangle className="inline mr-1" /> {error}
            </div>
          )}

          {!mapsReady && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm flex items-center gap-2">
              <span>⚠️</span>
              <span>Map service is loading or unavailable. You can still enter locations manually.</span>
            </div>
          )}

          {/* Quote Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Quote Type <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { value: 'abnormal', label: '🚛 Abnormal', desc: 'Specialized loads' },
                { value: 'minerals', label: '⛏️ Minerals', desc: 'Mining commodities' },
                { value: 'agri', label: '🌾 Agri', desc: 'Agricultural goods' },
                { value: 'general', label: '📋 General', desc: 'Other services' }
              ].map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, quote_type: type.value }))}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    formData.quote_type === type.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-xl mb-1">{type.label.split(' ')[0]}</div>
                  <div className="font-medium text-sm text-navy">{type.label.split(' ')[1]}</div>
                  <p className="text-xs text-gray-500 mt-1">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Type-specific fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {renderTypeFields()}
          </div>

          {/* Route Details - shown for minerals/agri/abnormal.
              Uses the same single InteractiveRouteMap as the admin Create Quote page. */}
          {(formData.quote_type === 'minerals' || formData.quote_type === 'agri' || formData.quote_type === 'abnormal') && (
            <>
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="text-lg font-medium text-navy mb-4">📍 Route Details</h3>
                {mapsReady ? (
                  <InteractiveRouteMap
                    originValue={formData.origin}
                    destinationValue={formData.destination}
                    onOriginChange={(val) => setFormData(prev => ({ ...prev, origin: val }))}
                    onDestinationChange={(val) => setFormData(prev => ({ ...prev, destination: val }))}
                    onDistanceCalculated={(data) => {
                      setFormData(prev => ({ ...prev, distance_km: data.distance_km }));
                    }}
                    onOriginCoordsChange={() => {}}
                    onDestCoordsChange={() => {}}
                    onRouteChange={(r) => setFormData(prev => ({ ...prev, route_optimized: r }))}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="origin"
                        value={formData.origin}
                        onChange={handleChange}
                        placeholder="Enter pickup location manually..."
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Delivery Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        placeholder="Enter delivery location manually..."
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Distance (km) <span className="text-xs text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="number"
                        name="distance_km"
                        value={formData.distance_km || ''}
                        onChange={handleChange}
                        placeholder="Enter estimated distance..."
                        className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                        style={{ borderColor: '#E8ECEF' }}
                        min="0"
                        step="0.1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ✅ PHASE 3: Site Info (optional upfront inspection) */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-medium text-navy mb-1">📋 Site Information</h3>
            <p className="text-sm text-gray-500 mb-4">
              Optional — helps us prepare an accurate quote faster. <span className="text-xs text-gray-400">(pre-site inspection)</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Is site access suitable for heavy vehicles?</label>
                <div className="flex gap-2">
                  {['Yes', 'No', 'Unsure'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setSiteInfo(prev => ({ ...prev, access: opt }))}
                      className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                        siteInfo.access === opt
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Offloading equipment on site?</label>
                <div className="flex gap-2">
                  {['Yes', 'No', 'Unsure'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setSiteInfo(prev => ({ ...prev, offloading: opt }))}
                      className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${
                        siteInfo.offloading === opt
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site contact person & phone</label>
                <input
                  type="text"
                  value={siteInfo.site_contact}
                  onChange={(e) => setSiteInfo(prev => ({ ...prev, site_contact: e.target.value }))}
                  placeholder="e.g. John (082 123 4567)"
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                  style={{ borderColor: '#E8ECEF' }}
                />
              </div>
            </div>

            {/* File upload */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📎 Upload Site Photos / Documents <span className="text-xs text-gray-400">(optional)</span>
              </label>
              <div
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-green-500 transition-colors"
                style={{ borderColor: '#E8ECEF' }}
                onClick={() => document.getElementById('client-site-files').click()}
              >
                <FaCamera className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to upload photos, PDFs, or site documents</p>
                <input
                  id="client-site-files"
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={handleSiteFilesSelected}
                />
              </div>

              {siteFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {siteFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 px-3">
                      <span className="text-sm text-gray-700 flex items-center gap-2">
                        <FaFileAlt className="text-gray-400" size={14} />
                        {file.name}
                      </span>
                      <button type="button" onClick={() => removeSiteFile(index)} className="text-red-500 text-sm hover:text-red-700">
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {uploadingFiles && (
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                  <FaSpinner className="animate-spin" /> Uploading files...
                </p>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleChange}
              rows="3"
              placeholder="Any special instructions or requirements..."
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <button
              type="button"
              onClick={() => navigate('/client-dashboard')}
              className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingFiles}
              className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#00A86B' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
              {loading ? 'Submitting...' : 'Submit Quote Request'}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-700">
          💡 Your quote request will be reviewed by One Purpose Logistics. You'll receive a notification 
          when your quote is ready, and you can view all your quotes under <strong>My Quotes</strong>.
        </p>
      </div>
    </ClientLayout>
  );
}

export default ClientNewJob;